import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, Image as ImageIcon, AlertCircle, Loader2 } from 'lucide-react'; // Added Loader2
import { Alert, AlertDescription } from '@/components/ui/alert';
import { compressImage } from '@/lib/utils/imageCompression'; // Import the compression utility

interface ImageUploaderProps {
  images: File[];
  setImages: React.Dispatch<React.SetStateAction<File[]>>;
  maxImages?: number;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE_MB_INITIAL_CHECK = 10; // Max size before attempting compression
const COMPRESSION_TARGET_MB = 0.3; // 300KB

const ImageUploader: React.FC<ImageUploaderProps> = ({ images, setImages, maxImages = 10 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isCompressing, setIsCompressing] = useState(false); // State for compression loading

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setErrorMessages([]); // Clear previous errors
    
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setErrorMessages([]); // Clear previous errors
    
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      // Reset file input to allow selecting the same file again after removal
      e.target.value = ''; 
    }
  };

  const handleFiles = async (files: FileList) => {
    const currentErrorMessages: string[] = [];
    const preValidatedFiles: File[] = [];

    Array.from(files).forEach(file => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        currentErrorMessages.push(`Formati i skedarit ${file.name} nuk suportohet. Ju lutemi përdorni JPG, PNG, GIF, ose WebP.`);
        return;
      }
      // Initial check for very large files before attempting compression
      if (file.size > MAX_FILE_SIZE_MB_INITIAL_CHECK * 1024 * 1024) {
        currentErrorMessages.push(`Skedari ${file.name} tejkalon limitin fillestar prej ${MAX_FILE_SIZE_MB_INITIAL_CHECK}MB dhe nuk do të procesohet.`);
        return;
      }
      preValidatedFiles.push(file);
    });

    if (preValidatedFiles.length === 0 && currentErrorMessages.length > 0) {
      setErrorMessages(currentErrorMessages);
      return;
    }
    
    if (preValidatedFiles.length === 0) {
        // No files to process after initial validation
        return;
    }

    setIsCompressing(true);
    setErrorMessages([]); // Clear previous errors before starting compression

    const compressedFilesPromises = preValidatedFiles.map(async (file) => {
      try {
        const compressedFile = await compressImage(file, { maxSizeMB: COMPRESSION_TARGET_MB });
        // Final check after compression (should ideally not be needed if compression works as expected)
        if (compressedFile.size > COMPRESSION_TARGET_MB * 1024 * 1024 * 1.1) { // Allow 10% tolerance
            currentErrorMessages.push(`Skedari i kompresuar ${compressedFile.name} është ende shumë i madh (${(compressedFile.size / 1024 / 1024).toFixed(2)}MB).`);
            return null;
        }
        return compressedFile;
      } catch (error: any) {
        console.error(`Failed to compress ${file.name}:`, error);
        currentErrorMessages.push(error.message || `Gabim gjatë kompresimit të imazhit ${file.name}.`);
        return null;
      }
    });

    const settledCompressedFiles = await Promise.all(compressedFilesPromises);
    const successfullyCompressedFiles = settledCompressedFiles.filter(file => file !== null) as File[];
    
    setIsCompressing(false);

    // Only add up to max images
    const availableSlots = maxImages - images.length;
    const filesToAdd = successfullyCompressedFiles.slice(0, availableSlots);
    
    if (successfullyCompressedFiles.length > filesToAdd.length && availableSlots > 0) {
        currentErrorMessages.push(`Mund të ngarkoni vetëm ${availableSlots} imazhe të tjera pas kompresimit.`);
    } else if (successfullyCompressedFiles.length > 0 && availableSlots <= 0) {
         currentErrorMessages.push(`Keni arritur numrin maksimal prej ${maxImages} imazhesh.`);
    }

    if (filesToAdd.length > 0) {
      setImages(prev => [...prev, ...filesToAdd]);
    }
    
    if (currentErrorMessages.length > 0) {
      setErrorMessages(prev => [...prev, ...currentErrorMessages]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setErrorMessages([]); 
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold mb-2">Imazhet e produktit</h3>
      
      {isCompressing && (
        <Alert variant="default" className="mb-4 bg-blue-50 border-blue-200 text-blue-800">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <AlertDescription>
            Po kompresohen imazhet, ju lutem prisni...
          </AlertDescription>
        </Alert>
      )}

      {errorMessages.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside">
              {errorMessages.map((msg, idx) => <li key={idx}>{msg}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {images.length >= maxImages && !isCompressing && !errorMessages.some(msg => msg.includes("numrin maksimal")) && (
        <Alert variant="default" className="mb-4 bg-amber-50 border-amber-200 text-amber-800">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Keni arritur numrin maksimal prej {maxImages} imazhesh që mund të ngarkoni.
          </AlertDescription>
        </Alert>
      )}
      
      {(images.length < maxImages && !isCompressing) && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-brand-400'
          } ${isCompressing ? 'opacity-50 pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !isCompressing && document.getElementById('image-upload')?.click()}
        >
          <div className="flex flex-col items-center justify-center">
            <ImagePlus className="h-12 w-12 text-gray-400 mb-2" />
            <p className="text-lg font-medium mb-1">Kliko ose tërhiq imazhe për ngarkim</p>
            <p className="text-sm text-gray-500">Formatet e suportuara: JPG, PNG, GIF, WebP. Madhësia maksimale pas kompresimit: {COMPRESSION_TARGET_MB}MB.</p>
            <p className="text-xs text-gray-400 mt-1">
              Mund të ngarkoni deri në {maxImages} imazhe ({images.length}/{maxImages} të ngarkuara)
            </p>
            <input
              id="image-upload"
              type="file"
              multiple
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={handleChange}
              className="hidden"
              disabled={images.length >= maxImages || isCompressing}
            />
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative border rounded-lg overflow-hidden group">
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                <img
                  src={URL.createObjectURL(image)}
                  alt={`Preview ${index}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <Button
                type="button" // Added type="button" here
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 opacity-70 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(index);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
              {index === 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-brand-500 text-white text-xs py-1 text-center">
                  Imazhi kryesor
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {images.length === 0 && errorMessages.length === 0 && !isCompressing && ( 
        <div className="rounded-lg p-4 bg-gray-50 text-center">
          <div className="flex flex-col items-center justify-center py-4">
            <ImageIcon className="h-10 w-10 text-gray-300 mb-2" />
            <p className="text-gray-500">Asnjë imazh i ngarkuar</p>
            <p className="text-xs text-gray-400">Imazhet janë shumë të rëndësishme për shitje të suksesshme</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
